// ═══════════════════════════════════════════════════════════════
// VolunteerOS — Comms
// Broadcast messaging to volunteers via email/SMS
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Stack, Button, TextInput, Textarea,
  Checkbox, Badge, Select, MultiSelect, Progress,
  Table, Tooltip, ActionIcon,
} from '@mantine/core';
import {
  IconSend, IconUsers, IconTemplate, IconTrash, IconClock,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import dayjs from 'dayjs';
import { useVolunteerStore } from '../../store/volunteerStore';
import type { Volunteer } from '../../store/volunteerStore';
import { useAuthStore } from '../../store/authStore';
import { WORKER_URL } from '../../lib/api-switch';

// ── Constants ────────────────────────────────────────────────

const WORKER = WORKER_URL;
const ORIGIN_HEADER = { 'Content-Type': 'application/json', Origin: 'https://apps.opengov.com' };

const TEMPLATES: { label: string; subject: string; body: string }[] = [
  {
    label: 'Activation Alert',
    subject: 'ACTIVATION ALERT — Volunteers Needed',
    body: 'This is an activation alert. Volunteers are needed for an active incident. Please check the VolunteerOS portal for available shifts and sign up as soon as possible. Your help is urgently needed.',
  },
  {
    label: 'Shift Reminder',
    subject: 'Shift Reminder — Upcoming Assignment',
    body: 'This is a reminder that you have an upcoming volunteer shift. Please review your assignment details in VolunteerOS and plan to arrive on time. Contact us if you need to make changes.',
  },
  {
    label: 'Thank You',
    subject: 'Thank You for Your Service',
    body: 'Thank you for your dedication and service. Your volunteer contributions make a real difference in our community. We truly appreciate your time and effort.',
  },
  {
    label: 'Weather Hold',
    subject: 'WEATHER HOLD — Operations Paused',
    body: 'Due to current weather conditions, volunteer operations are temporarily on hold. Please stand by for further instructions. Do not report to your assignment until the hold is lifted.',
  },
  {
    label: 'All Clear',
    subject: 'ALL CLEAR — Operations Resumed',
    body: 'The weather hold has been lifted and operations have resumed. Please check VolunteerOS for updated shift information and report as scheduled.',
  },
];

type PresetAudience = 'all_active' | 'deployed' | 'all';

interface SentMessage {
  timestamp: string;
  subject: string;
  recipientCount: number;
  channels: string[];
}

const LS_KEY = 'vol-comms-log';

function loadLog(): SentMessage[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) return JSON.parse(raw) as SentMessage[];
  } catch { /* ignore */ }
  return [];
}

function saveLog(log: SentMessage[]): void {
  localStorage.setItem(LS_KEY, JSON.stringify(log));
}

// ── Component ────────────────────────────────────────────────

export default function Comms() {
  const { volunteers, events, assignments, loadVolunteers, loadEvents, loadAssignments } = useVolunteerStore();
  const { incidents } = useAuthStore();

  // Initial load
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (!loaded) {
      setLoaded(true);
      loadVolunteers();
      loadEvents();
      loadAssignments();
    }
  }, [loaded, loadVolunteers, loadEvents, loadAssignments]);

  // Form state
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [presetAudience, setPresetAudience] = useState<PresetAudience>('all_active');
  const [selectedIncidentId, setSelectedIncidentId] = useState<string | null>(null);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [customVolIds, setCustomVolIds] = useState<string[]>([]);
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [smsEnabled, setSmsEnabled] = useState(false);

  // Sending state
  const [sending, setSending] = useState(false);
  const [sendProgress, setSendProgress] = useState(0);
  const [sendTotal, setSendTotal] = useState(0);

  // Log
  const [log, setLog] = useState<SentMessage[]>(() => loadLog());

  // ── Audience resolution ───────────────────────────────────

  const deployedVolIds = useMemo(() => {
    const confirmed = assignments.filter(a => a.status === 'confirmed');
    return new Set(confirmed.map(a => a.volunteerId));
  }, [assignments]);

  const recipients: Volunteer[] = useMemo(() => {
    // Start with preset audience
    let result: Volunteer[];
    if (presetAudience === 'all_active') result = volunteers.filter(v => v.status === 'active');
    else if (presetAudience === 'deployed') result = volunteers.filter(v => deployedVolIds.has(String(v.id)));
    else result = [...volunteers]; // 'all'

    // If incident selected, filter to volunteers assigned to that incident's events
    if (selectedIncidentId) {
      const incidentEventIds = new Set(
        events.filter(e => e.incidentId === selectedIncidentId).map(e => String(e.id))
      );
      const volIds = new Set(
        assignments.filter(a => incidentEventIds.has(a.eventId)).map(a => a.volunteerId)
      );
      result = result.filter(v => volIds.has(String(v.id)));
    }

    // If event selected, further filter to volunteers assigned to that specific event
    if (selectedEventId) {
      const volIds = new Set(
        assignments.filter(a => a.eventId === selectedEventId).map(a => a.volunteerId)
      );
      result = result.filter(v => volIds.has(String(v.id)));
    }

    // Union with custom-selected volunteers (additive)
    if (customVolIds.length > 0) {
      const resultIds = new Set(result.map(v => String(v.id)));
      const extras = volunteers.filter(v => customVolIds.includes(String(v.id)) && !resultIds.has(String(v.id)));
      result = [...result, ...extras];
    }

    return result;
  }, [presetAudience, selectedIncidentId, selectedEventId, customVolIds, volunteers, events, assignments, deployedVolIds]);

  // ── Template quick-fill ───────────────────────────────────

  const applyTemplate = useCallback((t: typeof TEMPLATES[number]) => {
    setSubject(t.subject);
    setBody(t.body);
  }, []);

  // ── Send ──────────────────────────────────────────────────

  const handleSend = useCallback(async () => {
    if (!subject.trim() || !body.trim()) {
      notifications.show({ title: 'Missing Fields', message: 'Subject and body are required.', color: 'red' });
      return;
    }
    if (recipients.length === 0) {
      notifications.show({ title: 'No Recipients', message: 'Select at least one recipient.', color: 'red' });
      return;
    }
    if (!emailEnabled && !smsEnabled) {
      notifications.show({ title: 'No Channel', message: 'Enable at least Email or SMS.', color: 'red' });
      return;
    }

    setSending(true);
    setSendProgress(0);
    setSendTotal(recipients.length);
    const channels: string[] = ['App'];
    if (emailEnabled) channels.push('Email');
    if (smsEnabled) channels.push('SMS');

    let sent = 0;
    let errors = 0;

    for (const vol of recipients) {
      const promises: Promise<void>[] = [];

      if (emailEnabled && vol.email) {
        promises.push(
          fetch(`${WORKER}/api/email`, {
            method: 'POST',
            headers: ORIGIN_HEADER,
            body: JSON.stringify({ to: vol.email, subject, body }),
          }).then(r => { if (!r.ok) throw new Error('email fail'); }).catch(() => { errors++; })
        );
      }

      if (smsEnabled && vol.phone) {
        promises.push(
          fetch(`${WORKER}/api/sms`, {
            method: 'POST',
            headers: ORIGIN_HEADER,
            body: JSON.stringify({ to: vol.phone, message: `${subject}\n\n${body}` }),
          }).then(r => { if (!r.ok) throw new Error('sms fail'); }).catch(() => { errors++; })
        );
      }

      await Promise.all(promises);
      sent++;
      setSendProgress(sent);
    }

    // Log the sent message
    const entry: SentMessage = {
      timestamp: new Date().toISOString(),
      subject,
      recipientCount: recipients.length,
      channels,
    };
    const updated = [entry, ...log].slice(0, 100);
    setLog(updated);
    saveLog(updated);

    setSending(false);
    if (errors > 0) {
      notifications.show({ title: 'Sent with Errors', message: `${sent} recipients processed, ${errors} delivery errors.`, color: 'yellow' });
    } else {
      notifications.show({ title: 'Sent', message: `Message delivered to ${sent} recipients.`, color: 'green' });
    }
    setSubject('');
    setBody('');
  }, [subject, body, recipients, emailEnabled, smsEnabled, log]);

  // ── Dropdowns ─────────────────────────────────────────────

  const incidentOptions = useMemo(() =>
    incidents.map(i => ({ value: String(i.id), label: i.name || `Incident #${i.id}` })),
  [incidents]);

  const eventOptions = useMemo(() => {
    const filtered = selectedIncidentId
      ? events.filter(e => e.incidentId === selectedIncidentId)
      : events;
    return filtered.map(e => ({ value: String(e.id), label: e.name || `Event #${e.id}` }));
  }, [events, selectedIncidentId]);

  const volunteerOptions = useMemo(() =>
    volunteers.map(v => ({ value: String(v.id), label: `${v.firstName} ${v.lastName}`.trim() || `Vol #${v.id}` })),
  [volunteers]);

  // ── Render ────────────────────────────────────────────────

  return (
    <Stack gap="lg">
      <Title order={3} c="black">Communications</Title>

      {/* Templates */}
      <Paper p="md" radius="sm" withBorder>
        <Text fw={600} size="sm" mb="xs">Quick Templates</Text>
        <Group gap="xs" wrap="wrap">
          {TEMPLATES.map(t => (
            <Button
              key={t.label}
              variant="light"
              size="xs"
              leftSection={<IconTemplate size={14} />}
              onClick={() => applyTemplate(t)}
            >
              {t.label}
            </Button>
          ))}
        </Group>
      </Paper>

      {/* Compose */}
      <Paper p="md" radius="sm" withBorder>
        <Stack gap="sm">
          <Text fw={600} size="sm">Compose Message</Text>
          <TextInput
            label="Subject"
            placeholder="Message subject"
            value={subject}
            onChange={e => setSubject(e.currentTarget.value)}
            required
          />
          <Textarea
            label="Body"
            placeholder="Message body..."
            value={body}
            onChange={e => setBody(e.currentTarget.value)}
            minRows={5}
            autosize
            required
          />
        </Stack>
      </Paper>

      {/* Audience */}
      <Paper p="md" radius="sm" withBorder>
        <Stack gap="sm">
          <Text fw={600} size="sm">Audience</Text>
          <Select
            label="Base Audience"
            data={[
              { value: 'all_active', label: 'All Active Volunteers' },
              { value: 'deployed', label: 'Currently Deployed' },
              { value: 'all', label: 'All Volunteers' },
            ]}
            value={presetAudience}
            onChange={v => setPresetAudience((v ?? 'all_active') as PresetAudience)}
          />

          <Select
            label="Filter by Incident"
            description="Optional — narrow recipients to this incident"
            placeholder="All incidents"
            data={incidentOptions}
            value={selectedIncidentId}
            onChange={v => {
              setSelectedIncidentId(v);
              // Clear event selection if it no longer belongs to this incident
              if (v && selectedEventId) {
                const validEventIds = new Set(
                  events.filter(e => e.incidentId === v).map(e => String(e.id))
                );
                if (!validEventIds.has(selectedEventId)) setSelectedEventId(null);
              }
            }}
            searchable
            clearable
          />

          <Select
            label="Filter by Event"
            description="Optional — narrow recipients to this event"
            placeholder="All events"
            data={eventOptions}
            value={selectedEventId}
            onChange={setSelectedEventId}
            searchable
            clearable
          />

          <MultiSelect
            label="Additional Volunteers"
            description="Optional — add specific volunteers to the recipient list"
            placeholder="Select volunteers to add"
            data={volunteerOptions}
            value={customVolIds}
            onChange={setCustomVolIds}
            searchable
          />

          <Group gap="xs">
            <IconUsers size={16} />
            <Text size="sm" fw={500}>
              {recipients.length} recipient{recipients.length !== 1 ? 's' : ''}
            </Text>
          </Group>
        </Stack>
      </Paper>

      {/* Channels + Send */}
      <Paper p="md" radius="sm" withBorder>
        <Stack gap="sm">
          <Text fw={600} size="sm">Channels</Text>
          <Group gap="lg">
            <Checkbox
              label="App Notification"
              checked
              disabled
              description="Always included"
            />
            <Checkbox
              label="Email"
              checked={emailEnabled}
              onChange={e => setEmailEnabled(e.currentTarget.checked)}
            />
            <Checkbox
              label="SMS"
              checked={smsEnabled}
              onChange={e => setSmsEnabled(e.currentTarget.checked)}
            />
          </Group>

          {sending && (
            <Stack gap={4}>
              <Text size="xs" c="dimmed">Sending {sendProgress} of {sendTotal}...</Text>
              <Progress value={sendTotal > 0 ? (sendProgress / sendTotal) * 100 : 0} animated />
            </Stack>
          )}

          <Button
            leftSection={<IconSend size={16} />}
            onClick={handleSend}
            loading={sending}
            disabled={!subject.trim() || !body.trim() || recipients.length === 0}
          >
            Send to {recipients.length} Recipient{recipients.length !== 1 ? 's' : ''}
          </Button>
        </Stack>
      </Paper>

      {/* Sent Messages Log */}
      <Paper p="md" radius="sm" withBorder>
        <Group justify="space-between" mb="sm">
          <Text fw={600} size="sm">Sent Messages</Text>
          {log.length > 0 && (
            <Tooltip label="Clear log">
              <ActionIcon
                variant="subtle"
                color="red"
                size="sm"
                onClick={() => { setLog([]); saveLog([]); }}
              >
                <IconTrash size={14} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>

        {log.length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="md">No messages sent yet.</Text>
        ) : (
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Time</Table.Th>
                <Table.Th>Subject</Table.Th>
                <Table.Th>Recipients</Table.Th>
                <Table.Th>Channels</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {log.map((m, i) => (
                <Table.Tr key={`${m.timestamp}-${i}`}>
                  <Table.Td>
                    <Group gap={4} wrap="nowrap">
                      <IconClock size={12} />
                      <Text size="xs">{dayjs(m.timestamp).format('MMM D, h:mm A')}</Text>
                    </Group>
                  </Table.Td>
                  <Table.Td><Text size="xs" lineClamp={1}>{m.subject}</Text></Table.Td>
                  <Table.Td><Badge size="sm" variant="light">{m.recipientCount}</Badge></Table.Td>
                  <Table.Td>
                    <Group gap={4}>
                      {m.channels.map(ch => (
                        <Badge key={ch} size="xs" variant="dot">{ch}</Badge>
                      ))}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>
    </Stack>
  );
}
